import React, { useState } from 'react';

const SortableTable = ({ 
    columns, 
    data, 
    emptyMessage = "No data available",
    className = "",
    containerClassName = ""
}) => {
    const [sortConfig, setSortConfig] = useState({
        key: null,
        direction: 'ascending'
    });

    const handleSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const sortedData = React.useMemo(() => {
        if (!sortConfig.key) return data;

        return [...data].sort((a, b) => {
            const aValue = a[sortConfig.key];
            const bValue = b[sortConfig.key];

            if (aValue === null || aValue === undefined) return 1;
            if (bValue === null || bValue === undefined) return -1;

            if (typeof aValue === 'string') {
                return sortConfig.direction === 'ascending'
                    ? aValue.localeCompare(bValue)
                    : bValue.localeCompare(aValue);
            }

            return sortConfig.direction === 'ascending'
                ? aValue - bValue
                : bValue - aValue;
        });
    }, [data, sortConfig]);

    const getSortIcon = (columnKey) => {
        if (sortConfig.key !== columnKey) {
            return (
                <span className="ml-1 text-gray-400">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                    </svg>
                </span>
            );
        }

        return (
            <span className="ml-1 text-blue-500">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                        d={sortConfig.direction === 'ascending' 
                            ? "M5 15l7-7 7 7" 
                            : "M19 9l-7 7-7-7"} 
                    />
                </svg>
            </span>
        );
    };

    return (
        <div className={`${containerClassName}`}>
            <div className="overflow-x-auto">
                {data.length === 0 ? (
                    <div className="text-left py-4 pl-6 text-gray-500">
                        {emptyMessage}
                    </div>
                ) : (
                    <table className={`min-w-full divide-y divide-gray-200 ${className}`}>
                        <thead className="bg-gray-50">
                            <tr>
                                {columns.map((column) => (
                                    <th
                                        key={column.key}
                                        className={`px-6 py-4 text-left text-1x1 whitespace-nowrap font-medium text-black-500 cursor-pointer hover:bg-gray-100 capitalize ${column.className || ''}`}
                                        onClick={() => handleSort(column.key)}
                                    >
                                        <div className="flex items-center">
                                            {column.label}
                                            {getSortIcon(column.key)}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {sortedData.map((item, index) => (
                                <tr key={index} className="hover:bg-gray-50">
                                    {columns.map((column) => (
                                        <td 
                                            key={column.key} 
                                            className={`px-6 py-4 whitespace-nowrap text-gray-600 ${column.className || ''}`}
                                        >
                                            {column.render ? column.render(item) : item[column.key]}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default SortableTable; 